import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useRoutes } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { SnackbarProvider } from 'notistack'
import parseISO from 'date-fns/parseISO'
import TagManager from 'react-gtm-module'

import Styled from 'styled'

import { testsServiceActions } from '@probatix/common/services/toolkit/tests'
import useLocaleAwareRoute from '@probatix/common/hooks/useLocaleAwareRoute'
import { getLocale } from '@probatix/common/utils/data'
import { Spinner } from '@probatix/common/components/atoms'
import i18n, { LOCALES } from '@probatix/common/translations'

import { SESSION_KEY } from 'config/app'
import Routing from 'core/routings'
import Meta from '@probatix/myprobatix/meta'
import useAuth from '@probatix/myprobatix/hooks/useAuth'

import { TitleProvider } from 'TitleProvider'

const App = () => {
  const { authenticated, checkAuth } = useAuth()
  const { pathname } = useLocation()
  const routes = useRoutes(Routing)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const lang = navigator.language // no ?s necessary
  const { generatePathWithLocale } = useLocaleAwareRoute()
  const [transInitialized, setTransIsInitialized] = useState<boolean>(false)

  if (!pathname.includes("activate") && !process.env.VITE_ENV?.startsWith("local")) {
    const tagManagerArgs = {
      gtmId: process.env.VITE_DEMO_ENV ? 'GTM-W7VL39X' : 'GTM-T2F3VPHG',
    }
    TagManager.initialize(tagManagerArgs)
  }

  const formData = window.sessionStorage.getItem(SESSION_KEY)
  if (formData) {
    const parsedData = JSON.parse(formData || '')
    dispatch(testsServiceActions.loadData({
      ...parsedData,
      time: parsedData.time && parseISO(parsedData.time),
    }))
  } else if (!formData && window.location.pathname.startsWith('/checkout/')) {
    navigate(generatePathWithLocale('checkout'))
  }

  useEffect(() => {
    const locale = getLocale(pathname, i18n)
    const langCode = lang.slice(0, 2)
    const preferredLang = Object.keys(LOCALES).includes(langCode) ? langCode : 'de'
    const urlSegmentCount = window.location.pathname.split('/')
    const index = Object.keys(LOCALES).findIndex((key) => `/${key}` === window.location.pathname)

    if ('/' === window.location.pathname || (2 >= urlSegmentCount.length && -1 !== index)) {
      i18n.changeLanguage(preferredLang)
      setTransIsInitialized(true)
      navigate(`${preferredLang}/signup`)
    } else if (locale) {
      i18n.changeLanguage(locale)
      setTransIsInitialized(true)
    }
  }, [])

  useEffect(() => {
    checkAuth()
  }, [])

  if (!transInitialized) {
    return <Spinner />
  }

  return (
    <SnackbarProvider 
      anchorOrigin={{
        horizontal: 'center',
        vertical: 'bottom',
      }}
      autoHideDuration={1500} 
      maxSnack={3} 
      variant="error"
    >
      <Meta lang="de" />
      <Styled authenticated={authenticated} />
      <TitleProvider>
        {routes}
      </TitleProvider>
    </SnackbarProvider>
  )
}

export default App
